/**
 * Everspring — Collection products page behaviours
 *
 * Three IIFEs for behaviours that used to be inline <script> tags inside
 * sections/es-collection-products.liquid:
 *   1. Category-tabs horizontal scroll + active filter chip handlers
 *   2. Scroll active category tab into view on page load
 *   3. Load-more button (AJAX pagination with pushState fallback)
 *
 * Extracted 2026-04-11 so these ~180 lines of JS become a cacheable asset
 * instead of inline code on every collection pageview.
 *
 * Each IIFE is independent and no-ops if its target element is missing,
 * so the module can load on any collection page without harm.
 */
(function () {
  'use strict';

  // Double-init guard — theme editor hot-reload, etc.
  if (window.__esCollectionProductsInit) return;
  window.__esCollectionProductsInit = true;

  /* ══════════════════════════════════════════════════════════════════
     1. Category tabs: horizontal-scroll state + active-chip handlers
     ══════════════════════════════════════════════════════════════════ */
(function () {
  var strip = document.querySelector('[data-es-cat-tabs]');
  if (!strip) return;
  var MQ = window.matchMedia('(max-width: 749px)');

  /* Hide-on-scroll-down was previously here — removed
     because the strip should always be visible when
     sticky-pinned. The user explicitly wants Filter +
     chips one tap away at all times, not auto-hidden. */

  /* ── Horizontal scroll: collapse Filter to icon-only ──── */
  var hTicking = false;
  function onHorizontalScroll() {
    if (hTicking) return;
    hTicking = true;
    requestAnimationFrame(function () {
      hTicking = false;
      strip.classList.toggle('es-collection__cat-tabs--is-h-scrolled', strip.scrollLeft > 6);
    });
  }
  strip.addEventListener('scroll', onHorizontalScroll, { passive: true });

  /* ── Active filter chip handlers ───────────────────────── */
  /* Tap chip → open drawer + scroll to that filter group     */
  /* Tap × → remove all values for that filter param          */
  document.addEventListener('click', function (e) {
    var openTrigger = e.target.closest && e.target.closest('[data-es-active-chip-open]');
    var removeTrigger = e.target.closest && e.target.closest('[data-es-active-chip-remove]');

    if (removeTrigger) {
      e.preventDefault();
      e.stopPropagation();
      var chip = removeTrigger.closest('[data-es-active-chip]');
      if (!chip) return;
      var paramName = chip.getAttribute('data-filter-param');
      if (!paramName) return;

      /* Build new search string with all params matching this
         filter param removed (handles multi-value filters too). */
      var current = new URLSearchParams(window.location.search);
      var keep = new URLSearchParams();
      current.forEach(function (val, key) {
        if (key !== paramName) keep.append(key, val);
      });
      var newSearch = keep.toString();

      /* Hand off to facets.js so it does its own fetch+render
         cycle (and so the URL updates via pushState). */
      if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
        FacetFiltersForm.renderPage(newSearch);
      } else {
        window.location.search = newSearch;
      }
      return;
    }

    if (openTrigger) {
      e.preventDefault();
      var chip2 = openTrigger.closest('[data-es-active-chip]');
      if (!chip2) return;
      var filterId = chip2.getAttribute('data-filter-id');

      /* Open the drawer. We delegate to the existing
         data-mobile-filter-open click handler by clicking
         the Filter button programmatically. */
      var filterBtn = document.querySelector('[data-mobile-filter-open]');
      if (filterBtn) filterBtn.click();

      /* After the drawer slide-up animation, scroll to the
         target filter group inside the drawer body and
         briefly highlight it. */
      setTimeout(function () {
        var target = document.getElementById(filterId);
        if (!target) return;
        var body = target.closest('.es-bsheet__body, .es-mobile-filter-drawer__body');
        if (body) {
          var topPos = target.offsetTop - body.offsetTop - 8;
          body.scrollTo({ top: topPos, behavior: 'smooth' });
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        target.classList.add('is-highlight-flash');
        setTimeout(function () {
          target.classList.remove('is-highlight-flash');
        }, 1100);
      }, 380);
    }
  });
})();

  /* ══════════════════════════════════════════════════════════════════
     2. Scroll the active category tab into view on page load
     ══════════════════════════════════════════════════════════════════ */
(function () {
  var nav = document.querySelector('[data-es-cat-tabs]');
  if (!nav) return;
  var active = nav.querySelector('.es-collection__cat-tab--active');
  if (active && typeof active.scrollIntoView === 'function') {
    // Only scroll horizontally inside the nav, not the page
    try { active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' }); }
    catch (e) { active.scrollIntoView({ inline: 'center', block: 'nearest' }); }
  }
})();

  /* ══════════════════════════════════════════════════════════════════
     3. Load-more button (AJAX pagination, pushState-aware)
     ══════════════════════════════════════════════════════════════════ */
(function () {
  // ── Load-more for the collection grid ───────────────────────────────────
  // Fetches the next paginated page in the background, parses out the new
  // <li> grid items and appends them to the existing #product-grid. The URL
  // is updated via history.pushState so the back button works. The classic
  // numbered pagination underneath stays as the SEO + deep-link fallback.

  var grid = document.getElementById('product-grid');
  var btn = document.querySelector('[data-load-more-btn]');
  if (!grid || !btn) return;

  var shownEl = document.querySelector('[data-load-more-shown]');
  var totalEl = document.querySelector('[data-load-more-total]');
  var labelEl = btn.querySelector('.es-collection__load-more-label');
  var origLabel = labelEl ? labelEl.textContent : btn.textContent;

  btn.addEventListener('click', function (e) {
    e.preventDefault();
    if (btn.disabled) return;
    var url = btn.dataset.nextUrl;
    if (!url) return;

    btn.disabled = true;
    btn.classList.add('is-loading');
    if (labelEl) labelEl.textContent = 'Laden…';

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.getElementById('product-grid');
        if (!newGrid) throw new Error('No #product-grid in response');

        // Append the new <li> items to the existing grid
        var newItems = newGrid.querySelectorAll(':scope > li');
        var frag = document.createDocumentFragment();
        newItems.forEach(function (li) { frag.appendChild(li); });
        grid.appendChild(frag);

        // Update the shown counter
        if (shownEl) {
          var current = parseInt(shownEl.textContent, 10) || 0;
          shownEl.textContent = current + newItems.length;
        }

        // Update the next-url from the freshly fetched page (or remove the
        // button entirely if there are no more pages)
        var newBtn = doc.querySelector('[data-load-more-btn]');
        if (newBtn && newBtn.dataset.nextUrl) {
          btn.dataset.nextUrl = newBtn.dataset.nextUrl;
          btn.disabled = false;
          btn.classList.remove('is-loading');
          if (labelEl) labelEl.textContent = origLabel;
        } else {
          btn.remove();
        }

        // Reflect the new URL in the address bar so the back button + share
        // links land the user on the same scroll-extended state.
        if (window.history && window.history.pushState) {
          window.history.pushState({ esLoadMore: true }, '', url);
        }
      })
      .catch(function (err) {
        console.error('[load-more] failed:', err);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = origLabel;
        // Fall back: navigate to the page directly so the user can still
        // reach the next page even if the AJAX request fails.
        window.location.href = url;
      });
  });
})();

})();
